import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import Login from "./pages/Login";
import { CompleteRegistration } from "./pages/Login/completeRegistration/CompleteRegistration";
import PrivateSignUp from "./pages/privateSignUp";
import PortalModule from "./pages/Portal/PortalModule";
import { isPortalHost } from "./pages/Portal/portalHost";
import { Logout } from "./pages/Users/modais/Logout";
import AppLayout from "./layouts/AppLayout";
import { RequirePermission } from "./components/RequirePermission";
import {
  canAccessKanban,
  canAccessKanbanV2,
  canAccessIss,
  canAccessBaseCadastral,
  canAccessBatchCertificates,
  canAccessCertidoes,
  canAccessDte,
  canAccessTaxCurrentAccount,
  canAccessPersona,
  canAccessCredentials,
} from "./routes/routeAccess";
import { lazy, type ComponentType } from "react";
// Guard de poucas linhas que envolve a rota de Certidões: estático de propósito (ver o arquivo).
import { LinkAntigoDoCadinFederal } from "./pages/TaxCurrentAccount/LinkAntigoDoCadinFederal";
// GEN-4080: guarda da rota /automacoes — estático pelo mesmo motivo (a página é sob demanda).
import { RequireAutomacoesAccess } from "./pages/Automacoes/RequireAutomacoesAccess";

// GEN-2959 (PERF-05): cada página da área autenticada vira um chunk próprio, carregado quando a rota
// é visitada. Antes, Kanban, Certidões, Caixa Postal, Configurações etc. iam todos no bundle inicial
// — inclusive para quem só abria o login. Login/logout/cadastro e o AppLayout continuam estáticos: são
// o primeiro paint. O `<Suspense>` que segura o carregamento fica em volta do `<Outlet/>` do AppShell.
// `named` adapta os exports nomeados ao `default` que o `lazy` exige.
const named = <K extends string, M extends Record<K, ComponentType<any>>>(load: () => Promise<M>, nome: K) =>
  lazy(() => load().then((m) => ({ default: m[nome] })));

const DashboardIndex = named(() => import("./pages/Dashboard/DashboardIndex"), "DashboardIndex");
const NotificationPanel = named(() => import("./pages/NotificationPanel/NotificationPanel"), "NotificationPanel");
const HistoryPanel = named(() => import("./pages/HistoryPanel/HistoryPanel"), "HistoryPanel");
const TasksPanel = named(() => import("./pages/TasksPanel/TasksPanel"), "TasksPanel");
const TaxKanban = named(() => import("./pages/TaxKanban/TaxKanban"), "TaxKanban");
const TaxKanbanV2 = named(() => import("./pages/TaxKanbanV2/TaxKanbanV2"), "TaxKanbanV2");
const Automacoes = named(() => import("./pages/Automacoes/Automacoes"), "Automacoes");
const IssModule = named(() => import("./pages/Iss/IssModule"), "IssModule");
const BaseCadastralModule = named(() => import("./pages/BaseCadastral/BaseCadastralModule"), "BaseCadastralModule");
const BatchCertificatesOnDemandPanel = named(
  () => import("./pages/BatchCertificatesOnDemand/BatchCertificatesOnDemandPanel"),
  "BatchCertificatesOnDemandPanel"
);
const CertificateManagerPanel = named(
  () => import("./pages/CNDManager/CertificateManagerPanel"),
  "CertificateManagerPanel"
);
const DtePanel = named(() => import("./pages/Dte/DtePanel"), "DtePanel");
// Conta Corrente (GEN-3984): o módulo com as sub-páginas é lazy, para o TaxCurrentAccountPanel não voltar
// ao bundle inicial pelo import estático.
const ContaCorrenteModule = named(
  () => import("./pages/TaxCurrentAccount/ContaCorrenteModule"),
  "ContaCorrenteModule"
);
const Company = named(() => import("./pages/Companies/Company"), "Company");
const PersonaPanel = named(() => import("./pages/Persona/PersonaPanel"), "PersonaPanel");
const UserPanel = named(() => import("./pages/Users/UserPanel"), "UserPanel");
const Credentials = named(() => import("./pages/Credentials/Credentials"), "Credentials");
const ConfigPanel = named(() => import("./pages/ConfigPanel/ConfigPanel"), "ConfigPanel");

const App = () => {
  // GEN-3604: em `autosservico.taxly.com.br` (ou `portal.`) o MESMO build serve só o portal, na raiz —
  // nenhuma rota da plataforma existe nesse host. Ver `pages/Portal/portalHost.ts`.
  if (isPortalHost()) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/*" element={<PortalModule />} />
        </Routes>
      </BrowserRouter>
    );
  }
  return (
    <BrowserRouter>
      <Routes>
        {/* Públicas */}
        <Route path="login" element={<Login />} />
        <Route path="complete-registration/:hash" element={<CompleteRegistration />} />
        <Route path="logout" element={<Logout />} />
        {/* Fluxo de signup em tela cheia: fica fora do AppLayout para não
            renderizar dentro do shell do dashboard (Sidebar + mainPage). */}
        <Route path="private-signup" element={<PrivateSignUp />} />
        {/* GEN-3604: Portal de Autosserviço de Certidões — superfície própria para as áreas
            "clientes" dos nossos usuários. Fora do AppLayout de propósito: zero navegação da
            plataforma interna acessível por aqui (login próprio, busca e download). */}
        <Route path="portal/*" element={<PortalModule />} />

        {/* Área autenticada: layout pathless (auth + estado + Sidebar + Outlet).
            Todas as páginas são rotas irmãs no mesmo nível; a dashboard é o índice. */}
        <Route element={<AppLayout />}>
          <Route index element={<DashboardIndex />} />
          <Route
            path="empresas"
            element={
              <RequirePermission p="company.read">
                <Company />
              </RequirePermission>
            }
          />
          <Route
            path="usuarios"
            element={
              <RequirePermission p="user.read">
                <UserPanel />
              </RequirePermission>
            }
          />
          <Route
            path="certidoes"
            element={
              <LinkAntigoDoCadinFederal>
                <RequirePermission p="cnd.read" extra={canAccessCertidoes}>
                  <CertificateManagerPanel />
                </RequirePermission>
              </LinkAntigoDoCadinFederal>
            }
          />
          <Route
            path="caixa-postal"
            element={
              <RequirePermission p="dte.read" extra={canAccessDte}>
                <DtePanel />
              </RequirePermission>
            }
          />
          {/* Conta Corrente (GEN-3984): módulo com sub-páginas — Situação Fiscal e CADIN Federal. */}
          <Route
            path="conta-corrente/*"
            element={
              <RequirePermission p="taxCurrentAccount.read" extra={canAccessTaxCurrentAccount}>
                <ContaCorrenteModule />
              </RequirePermission>
            }
          />
          <Route
            path="kanban"
            element={
              <RequirePermission p="kanban.read" extra={canAccessKanban}>
                <TaxKanban />
              </RequirePermission>
            }
          />
          {/* Kanban 2.0 — módulo externo (@taxlybr/kanban2-web) montado in-tree num React
              root separado. Rota com /* p/ o router interno do módulo (basename /kanban-v2).
              GEN-3121: gate por kanban2.read (o fallback legado kanban2.* ⇐ kanban.* foi REMOVIDO no BE —
              K2 e Kanban de Obrigações são módulos separados) + product kanban2 (flag de rollout por cliente). */}
          <Route
            path="kanban-v2/*"
            element={
              <RequirePermission p="kanban2.read" extra={canAccessKanbanV2}>
                <TaxKanbanV2 />
              </RequirePermission>
            }
          />
          {/* Automações — módulo PRÓPRIO, fora do Kanban. O motor reage a fatos da Caixa Postal,
              de certidões, de Conta Corrente, de dívida ativa e da situação cadastral; a tela
              continua no pacote do K2 e é montada aqui por `mountAutomacoes` (mesmo padrão do
              quadro). `?modulo=` filtra por produto — é o que os botões dentro de cada módulo usam.
              GEN-4080: `?tipo=alertas` também abre para quem só tem `alerts.read` (podeAbrirAutomacoes). */}
          <Route
            path="automacoes"
            element={
              <RequireAutomacoesAccess>
                <Automacoes />
              </RequireAutomacoesAccess>
            }
          />
          {/* Taxly ISS (GEN-2972) — port do piloto Statkraft: telas nativas do host,
              rotas internas em IssModule. Gate: iss.read + product iss (por cliente). */}
          <Route
            path="iss/*"
            element={
              <RequirePermission p="iss.read" extra={canAccessIss}>
                <IssModule />
              </RequirePermission>
            }
          />
          {/* Base Cadastral (GEN-3162) — dados e documentos societários; rotas internas
              em BaseCadastralModule. Gate: registry.read + product registry (por cliente). */}
          <Route
            path="base-cadastral/*"
            element={
              <RequirePermission p="registry.read" extra={canAccessBaseCadastral}>
                <BaseCadastralModule />
              </RequirePermission>
            }
          />
          <Route
            path="consultas-em-lote"
            element={
              <RequirePermission p="batchCertificatesOnDemand.read" extra={canAccessBatchCertificates}>
                <BatchCertificatesOnDemandPanel />
              </RequirePermission>
            }
          />
          {/* GEN-4101 — cada aviso tem endereço próprio (voltar, atualizar e compartilhar funcionam). */}
            <Route path="notificacoes/:guid?" element={<NotificationPanel />} />
          <Route path="historico" element={<HistoryPanel />} />
          <Route
            path="tarefas"
            element={
              <RequirePermission p="kanban.read" extra={canAccessKanban}>
                <TasksPanel />
              </RequirePermission>
            }
          />
          <Route
            path="pessoa-fisica"
            element={
              <RequirePermission p="persona.read" extra={canAccessPersona}>
                <PersonaPanel />
              </RequirePermission>
            }
          />
          <Route
            path="credenciais"
            element={
              <RequirePermission p="credential.read" extra={canAccessCredentials}>
                <Credentials />
              </RequirePermission>
            }
          />
          {/* GEN-3497 (review r5): esta rota exigia `config.read`, que no catálogo de
              permissões é a permissão de ADMINISTRAR o cliente (alterar recorrência, ativar
              consultas, definir a régua do "A Vencer"). Só que a mesma tela é o único lugar
              onde a pessoa edita o próprio cadastro e troca a própria senha — e, com a barra
              global, o único caminho até isso. Um colaborador operacional ficava sem trocar a
              senha por UI nenhuma.

              A tela já estava preparada: a barra lateral do painel mostra "Minha conta",
              "Aparência" e "Notificações" para todos, e guarda o bloco "Configurações de
              produtos" atrás de `config.read`. O ConfigPanel agora aplica o mesmo recorte ao
              CONTEÚDO, para que `?secao=certificate` não alcance a administração por URL. */}
          {/* GEN-3670 (r8): segmento opcional — `/configuracoes/base-cadastral` abre a seção direto, e a
              mesma rota (sem remontar a tela) atende `/configuracoes` e o `?secao=` antigo. */}
          <Route path="configuracoes/:secao?" element={<ConfigPanel />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default App;
